import { CenterSpinner, LazyImage } from '@mr-yum/frontend-ui'
import { NotFound } from 'components/Common/NotFound'
import { Image } from 'components/Shared/Image'
import { CollectionVenueCard } from 'components/Venue/shared'
import {
  useVenueCollectionBySlugQuery,
  useVenueCollectionRegionQuery,
  VctvType,
} from 'lib/gql'
import { getOrderingSlugFromType } from 'lib/routes'
import React from 'react'
import { useIntl } from 'react-intl'

import { LPVenueCollectionRouteLink } from './LPVenueCollectionRouteLink'
import { LPVenueRouteLink } from './LPVenueRouteLink'

interface Props {
  vcslug: string
}

export const LandingPageComponent = ({ vcslug }: Props) => {
  const intl = useIntl()

  const [{ data, error, fetching }] = useVenueCollectionBySlugQuery({
    variables: { vcslug },
  })

  const [{ data: regionData }] = useVenueCollectionRegionQuery()

  if (fetching) {
    return <CenterSpinner />
  }

  const vcRegion = regionData?.venueCollectionRegion
  const venueCollection = data?.venueCollectionBySlug
  const venueCollectionItems =
    venueCollection?.venueCollectionToVenue?.filter((item) => item.enabled) ||
    []

  if (
    error ||
    (!fetching && !venueCollection) ||
    (!fetching && venueCollectionItems.length === 0)
  ) {
    return <NotFound error={new Error(`Landing Page ${vcslug} not found`)} />
  }

  return (
    <div className="mx-auto w-full max-w-[480px] px-2">
      <div className="flex min-h-screen flex-col pb-6 pt-10">
        <h2 className="text-center my-heading-md">
          {venueCollection?.name || ''}
        </h2>
        <div className="my-12 flex max-h-[160px] w-full justify-center">
          <h1 className="text-center my-heading-xl">
            {venueCollection?.description ||
              intl.formatMessage({
                defaultMessage: 'Where are you ordering from?',
                id: '93ZwIM',
              })}
          </h1>
        </div>
        {venueCollectionItems.map(
          (
            { type, venue, childrenCollection, orderingType, name, image },
            index,
          ) => {
            if (
              (type === VctvType.VenueCollection && !childrenCollection) ||
              (type === VctvType.Venue && !venue)
            ) {
              return null
            }

            if (type === VctvType.VenueCollection && childrenCollection) {
              return (
                <LPVenueCollectionRouteLink
                  key={index}
                  vcregion={vcRegion}
                  vcslug={childrenCollection.slug}
                >
                  <div className="block hover:text-white hover:underline active:text-white active:underline">
                    <CollectionVenueCard
                      image={
                        image?.originalImageUrl ? (
                          <Image
                            image={image}
                            layout="fill"
                            sizes="500px"
                            alt={childrenCollection.name}
                          />
                        ) : (
                          <LazyImage
                            src="/static/landing_page/landing-page-default.jpg"
                            sizes="500px"
                            alt={childrenCollection.name}
                          />
                        )
                      }
                      title={name ? name : childrenCollection.name}
                    />
                  </div>
                </LPVenueCollectionRouteLink>
              )
            }

            if (!venue) return null

            const altText = `${venue.name} ${orderingType || ''}`

            return (
              <LPVenueRouteLink
                key={index}
                venueSlug={venue.slug}
                orderingType={
                  orderingType
                    ? getOrderingSlugFromType(orderingType)
                    : undefined
                }
              >
                <div className="block hover:text-white hover:underline active:text-white active:underline">
                  <CollectionVenueCard
                    image={
                      image?.originalImageUrl ? (
                        <Image
                          image={image}
                          layout="fill"
                          sizes="500px"
                          alt={altText}
                        />
                      ) : (
                        <LazyImage
                          src="/static/landing_page/landing-page-default.jpg"
                          sizes="500px"
                          alt={altText}
                        />
                      )
                    }
                    title={name ? name : venue.name}
                  />
                </div>
              </LPVenueRouteLink>
            )
          },
        )}
      </div>
    </div>
  )
}
